import {
  AdjustmentsHorizontalIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  CalendarIcon,
  ChevronDownIcon,
  FlagIcon,
  InformationCircleIcon,
  MapPinIcon,
} from "@heroicons/react/24/outline";
import { DateTime } from "luxon";
import { observer } from "mobx-react-lite";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DataTable, { TableStyles } from "react-data-table-component";
import { apiService } from "../ApiService";
import MultiSelectDropdown from "../components/MultiSelectDropdown";
import SearchBar from "../components/SearchBar";
import SidebarPageLayout from "../components/SidebarPageLayout";
import { MetricType } from "../stores/GoalStore";
import { rootStore } from "../stores/RootStore";

// Define types for our data
type RowData = {
  id: string;
  name: string;
  level: number;
  parent?: string;
  isExpanded?: boolean;
  isLoading?: boolean;
  metricType?: string; // Neues Feld für Metriktyp
  tariffName?: string; // Neues Feld für Tarifnamen
  isGroupRow?: boolean; // Neues Feld für Tarifgruppen-Zeilen
  hasOverlaps?: boolean; // Neues Feld für Überschneidungen zwischen Gruppen
  [key: string]: any; // For dynamic month columns
};

// Hilfsfunktion zur Umwandlung von MetricType in API-String
function metricTypeToApiString(metricType: MetricType): string {
  switch (metricType) {
    case MetricType.NEW_MEMBERS:
      return "new-members";
    case MetricType.NEW_MEMBERS_ACTIVE:
      return "new-members-active";
    case MetricType.CANCELLATIONS_ML:
      return "cancellations-ml";
    case MetricType.CANCELLATIONS_AT:
      return "cancellations-at";
    case MetricType.INCOMING_CANCELLATIONS:
      return "incoming-cancellations";
    case MetricType.NET_GROWTH:
      return "members-net-growth-started";
    case MetricType.NET_GROWTH_FORECAST:
      return "members-net-growth-created";
    case MetricType.NET_GROWTH_PERCENTAGE_FROM_START:
      return "members-net-growth-percentage-from-start";
    case MetricType.NET_GROWTH_ABSOLUTE_FROM_START:
      return "members-net-growth-absolute-from-start";
    case MetricType.NET_GROWTH_AT_EFFECTIVE:
      return "members-net-growth-at-effective";
    case MetricType.NET_GROWTH_AT_INCOMING:
      return "members-net-growth-at-incoming";
    case MetricType.NEW_MEMBERS_WITH_NAMES:
      return "new-members-with-names";
    case MetricType.NEW_CLOSINGS_WITH_NAMES:
      return "new-closings-with-names";
    case MetricType.MODULE_CONTRACTS_WITH_NAMES:
      return "module-contracts-with-names";
    default:
      return "";
  }
}

// Hilfsfunktion zum Chunking von Arrays
function chunkArray<T>(arr: T[], chunkSize: number): T[][] {
  const result: T[][] = [];
  for (let i = 0; i < arr.length; i += chunkSize) {
    result.push(arr.slice(i, i + chunkSize));
  }
  return result;
}

// Convert the component to use observer
const AnalyticsPage: React.FC = observer(() => {
  // State for date range
  const [startDate, setStartDate] = useState<string>(() => {
    const savedStartDate = localStorage.getItem("analyticsStartDate");
    if (savedStartDate) return savedStartDate;
    // Erster Tag des aktuellen Monats
    return DateTime.now().startOf("month").toISODate();
  });
  const [endDate, setEndDate] = useState<string>(() => {
    const savedEndDate = localStorage.getItem("analyticsEndDate");
    if (savedEndDate) return savedEndDate;
    // Letzter Tag des aktuellen Monats
    return DateTime.now().endOf("month").toISODate();
  });

  // State for search term
  const [searchTerm, setSearchTerm] = useState("");

  // Add state for tariff grouping
  const [tariffGrouping, setTariffGrouping] = useState<string>(() => {
    const savedGrouping = localStorage.getItem("analyticsTariffGrouping");
    return savedGrouping || "";
  });

  // Add state for selected tariff groupings
  const [selectedTariffGroupings, setSelectedTariffGroupings] = useState<string[]>(() => {
    const savedGroupings = localStorage.getItem("analyticsSelectedTariffGroupings");
    return savedGroupings ? JSON.parse(savedGroupings) : [];
  });

  // Add time range state
  const [timeRange, setTimeRange] = useState<"day" | "week" | "month">(() => {
    const savedTimeRange = localStorage.getItem("analyticsTimeRange");
    return (savedTimeRange as "day" | "week" | "month") || "month";
  });

  // Memoize the months calculation
  const getTimeRangeLabels = (start: string, end: string): string[] => {
    const labels: string[] = [];
    const startDate = DateTime.fromISO(start);
    const endDate = DateTime.fromISO(end);

    let currentDate = startDate;

    while (currentDate <= endDate) {
      switch (timeRange) {
        case "day":
          labels.push(currentDate.toFormat("dd.MM"));
          currentDate = currentDate.plus({ days: 1 });
          break;
        case "week":
          const weekLabel = `KW ${currentDate.weekNumber}`;
          if (!labels.includes(weekLabel)) {
            labels.push(weekLabel);
          }
          currentDate = currentDate.plus({ days: 1 });
          break;
        case "month":
          labels.push(currentDate.toFormat("LLL"));
          currentDate = currentDate.plus({ months: 1 });
          break;
      }
    }

    return labels;
  };

  const timeLabels = useMemo(() => getTimeRangeLabels(startDate, endDate), [startDate, endDate, timeRange, getTimeRangeLabels]);

  // Initialisiere mit leerem Array statt Dummy-Daten
  const [data, setData] = useState<RowData[]>([]);

  // Function to toggle row expansion
  const toggleRowExpansion = (rowId: string) => {
    setData((prevData) => prevData.map((row) => (row.id === rowId ? { ...row, isExpanded: !row.isExpanded } : row)));
  };

  // Filter visible rows based on search term and expansion state
  const getVisibleRows = () => {
    const visibleRows: RowData[] = [];
    const expandedParents = new Set(data.filter((row) => row.level === 0 && row.isExpanded).map((row) => row.id));
    const expandedMetrics = new Set(data.filter((row) => row.level === 1 && row.isExpanded).map((row) => row.id));

    // First filter by search term if one exists
    let filteredParents = data.filter((row) => row.level === 0);
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filteredParents = filteredParents.filter((row) => {
        if (row.id.startsWith("group-")) {
          const group = groupStore.groups.find((g) => `group-${g.id}` === row.id);
          return group?.groupName?.toLowerCase().includes(searchLower);
        }
        return row.name.toLowerCase().includes(searchLower);
      });
    }

    // Sortiere die Eltern nach der locationOrder
    filteredParents.sort((a, b) => {
      const aIndex = locationOrder.indexOf(a.id);
      const bIndex = locationOrder.indexOf(b.id);
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return a.name.localeCompare(b.name, "de");
    });

    // Then apply expansion filtering
    filteredParents.forEach((parent) => {
      visibleRows.push(parent);
      if (expandedParents.has(parent.id)) {
        // Add all metric children of this parent
        const metricChildren = data.filter((row) => row.parent === parent.id && row.level === 1);
        metricChildren.forEach((metric) => {
          visibleRows.push(metric);
          // If this metric is expanded and has tariff children, add them
          if (expandedMetrics.has(metric.id)) {
            const tariffChildren = data.filter((row) => row.parent === metric.id && row.level === 2);
            visibleRows.push(...tariffChildren);
          }
        });
      }
    });

    return visibleRows;
  };

  // Generate columns for the table
  const generateColumns = () => {
    const columns = [
      {
        name: "Name",
        selector: (row: RowData) => row.name,
        sortable: false,
        cell: (row: RowData) => (
          <div
            className="flex cursor-pointer"
            style={{ paddingLeft: `${row.level * 32}px` }}
            onClick={() => {
              if (row.level === 0) {
                toggleRowExpansion(row.id);
              } else if (row.level === 1 && (row.metricType === MetricType.NEW_MEMBERS_WITH_NAMES.toString() || row.metricType === MetricType.NEW_CLOSINGS_WITH_NAMES.toString() || row.metricType === MetricType.MODULE_CONTRACTS_WITH_NAMES.toString())) {
                toggleRowExpansion(row.id);
              }
            }}
          >
            {row.level === 0 ||
            (row.level === 1 && (row.metricType === MetricType.NEW_MEMBERS_WITH_NAMES.toString() || row.metricType === MetricType.NEW_CLOSINGS_WITH_NAMES.toString() || row.metricType === MetricType.MODULE_CONTRACTS_WITH_NAMES.toString())) ? (
              <ChevronDownIcon className={`w-4 h-4 mr-2 transition-transform ${row.isExpanded ? "rotate-180" : ""}`} />
            ) : row.level > 0 ? (
              <svg className="w-4 h-4 mr-2" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ transform: "scaleX(-1)" }}>
                <path d="M0 8H12M12 8V0" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            ) : null}
            <span>{row.level === 0 && row.id.startsWith("group-") ? groupStore.groups.find((g) => `group-${g.id}` === row.id)?.groupName || "Unbekannt" : row.name}</span>
            {/* Show info icon for groups with overlaps */}
            {row.isGroupRow && row.hasOverlaps && (
              <div
                className="tooltip tooltip-right ml-2"
                data-tip="Diese Gruppe enthält Einträge die in anderen Gruppen vorkommen"
                style={
                  {
                    "--tooltip-color": "#1f1f1f",
                    "--tooltip-text-color": "#e2e2e2",
                  } as React.CSSProperties
                }
              >
                <InformationCircleIcon className="w-4 h-4 text-yellow-500" />
              </div>
            )}
          </div>
        ),
        width: "250px",
      },
    ];

    // Add a column for each time label
    timeLabels.forEach((label) => {
      columns.push({
        name: label,
        selector: (row: RowData) => row[label] || 0,
        sortable: false,
        cell: (row: RowData) => {
          if (row.level === 0) {
            return <div className="w-full"></div>;
          }
          if (row.isLoading) {
            return <div className="loading loading-spinner loading-xs"></div>;
          }
          return <div className="w-full">{row[label]?.toLocaleString() || "0"}</div>;
        },
        width: "150px",
      });
    });

    // Add single sum column
    columns.push({
      name: "Summe",
      selector: (row: RowData) => {
        // Calculate sum for metric rows (level 1) and tariff rows (level 2)
        if (row.level === 1 || row.level === 2) {
          return timeLabels.reduce((sum, label) => sum + (row[label] || 0), 0).toString();
        }
        return "";
      },
      sortable: false,
      cell: (row: RowData) => {
        if (row.level === 0) {
          return <div className="w-full"></div>;
        }
        // Show loading spinner for metrics that are loading
        if (row.isLoading) {
          return <div className="loading loading-spinner loading-xs"></div>;
        }

        // Show sum for metric rows and tariff rows
        if (row.level === 1 || row.level === 2) {
          const sum = timeLabels.reduce((sum, label) => sum + (row[label] || 0), 0);
          return <div className="w-full font-medium">{sum.toLocaleString()}</div>;
        }
        return <div></div>;
      },
      width: "150px",
    });

    return columns;
  };

  // State für Multiselect-Dropdowns
  const [selectedFilters, setSelectedFilters] = useState<string>(() => {
    const savedFilters = localStorage.getItem("analyticsSelectedFilters");
    return savedFilters ? JSON.parse(savedFilters)[0] || "" : "";
  });
  const [selectedLocations, setSelectedLocations] = useState<string[]>(() => {
    const savedLocations = localStorage.getItem("analyticsSelectedLocations");
    return savedLocations ? JSON.parse(savedLocations) : [];
  });

  // State for metrics dropdown - initialize with first metric selected
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>(() => {
    const savedMetrics = localStorage.getItem("analyticsSelectedMetrics");
    return savedMetrics ? JSON.parse(savedMetrics) : [];
  });
  const [isMetricsDropdownOpen, setIsMetricsDropdownOpen] = useState(false);
  const [isTimeRangeDropdownOpen, setIsTimeRangeDropdownOpen] = useState(false);

  // Cache für bereits geladene Daten, um unnötige API-Calls zu vermeiden
  const [metricsDataCache, setMetricsDataCache] = useState<Record<string, any>>({});

  // Flag, um Bulk-Operationen zu kennzeichnen (z.B. wenn alle Studios und KPIs neu geladen werden müssen)
  const [isBulkOperation, setIsBulkOperation] = useState(false);

  // Add state for metric order
  const [metricOrder, setMetricOrder] = useState<MetricType[]>(() => {
    const defaultMetrics = [
      MetricType.NEW_MEMBERS,
      MetricType.NEW_MEMBERS_ACTIVE,
      MetricType.NEW_MEMBERS_WITH_NAMES,
      MetricType.NEW_CLOSINGS_WITH_NAMES,
      MetricType.MODULE_CONTRACTS_WITH_NAMES,
      MetricType.CANCELLATIONS_ML,
      MetricType.CANCELLATIONS_AT,
      MetricType.INCOMING_CANCELLATIONS,
      MetricType.NET_GROWTH,
      MetricType.NET_GROWTH_FORECAST,
      MetricType.NET_GROWTH_PERCENTAGE_FROM_START,
      MetricType.NET_GROWTH_ABSOLUTE_FROM_START,
      MetricType.NET_GROWTH_AT_EFFECTIVE,
      MetricType.NET_GROWTH_AT_INCOMING,
    ];

    const savedOrder = localStorage.getItem("analyticsMetricOrder");
    if (savedOrder) {
      try {
        const parsedOrder = JSON.parse(savedOrder);
        // Prüfe ob die Arrays exakt die gleichen Einträge haben
        if (!Array.isArray(parsedOrder) || parsedOrder.length !== defaultMetrics.length || !defaultMetrics.every((metric) => parsedOrder.includes(metric))) {
          localStorage.removeItem("analyticsMetricOrder");
          return defaultMetrics;
        }
        return parsedOrder;
      } catch (e) {
        localStorage.removeItem("analyticsMetricOrder");
        return defaultMetrics;
      }
    }
    return defaultMetrics;
  });

  // Save metric order to localStorage when it changes
  useEffect(() => {
    localStorage.setItem("analyticsMetricOrder", JSON.stringify(metricOrder));
  }, [metricOrder]);

  // Get stores for data
  const { customerStore, groupStore, filterStore, goalStore, tariffGroupingStore } = rootStore;

  // Add state for location order
  const [locationOrder, setLocationOrder] = useState<string[]>(() => {
    try {
      const savedOrder = localStorage.getItem("analyticsLocationOrder");
      if (savedOrder) {
        const parsed = JSON.parse(savedOrder);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      }
    } catch (e) {
      console.error("Fehler beim Laden der Location-Sortierung:", e);
    }
    return [];
  });

  // Effect für die Aktualisierung der Sortierung wenn sich die verfügbaren Standorte ändern
  useEffect(() => {
    if (customerStore.studios.length === 0 && groupStore.groups.length === 0) {
      return;
    }

    const allAvailableLocations = [
      ...customerStore.studios.map((studio) => ({
        value: `studio-${studio.id}`,
        label: studio.name,
      })),
      ...groupStore.groups.map((group) => ({
        value: `group-${group.id}`,
        label: group.groupName,
      })),
    ];

    const allLocationsValues = allAvailableLocations.map((loc) => loc.value);

    // Nur aktualisieren wenn die Sortierung leer ist
    if (locationOrder.length === 0) {
      const newOrder = [...allAvailableLocations].sort((a, b) => a.label.localeCompare(b.label, "de")).map((loc) => loc.value);

      setLocationOrder(newOrder);
      localStorage.setItem("analyticsLocationOrder", JSON.stringify(newOrder));
      return;
    }

    // Prüfe ob neue Standorte hinzugekommen sind
    const missingLocations = allLocationsValues.filter((loc) => !locationOrder.includes(loc));
    if (missingLocations.length > 0) {
      const newOrder = [...locationOrder, ...missingLocations];
      setLocationOrder(newOrder);
      localStorage.setItem("analyticsLocationOrder", JSON.stringify(newOrder));
    }
  }, [customerStore.studios, groupStore.groups, locationOrder]);

  // Handle location reordering
  const handleLocationReorder = (draggedLocation: string, targetLocation: string) => {
    const newOrder = [...locationOrder];
    const draggedIndex = newOrder.indexOf(draggedLocation);
    const targetIndex = newOrder.indexOf(targetLocation);

    if (draggedIndex !== -1 && targetIndex !== -1) {
      newOrder.splice(draggedIndex, 1);
      newOrder.splice(targetIndex, 0, draggedLocation);
      setLocationOrder(newOrder);
      localStorage.setItem("analyticsLocationOrder", JSON.stringify(newOrder));
    }
  };

  // Track loading states
  const [isLoading, setIsLoading] = useState({
    filters: true,
    locations: true,
  });

  // Add abort controller ref - ein Controller pro Studio/KPI-Kombination
  const abortControllersRef = useRef<Record<string, AbortController>>({});

  // Load studios, groups and filters data
  useEffect(() => {
    const loadData = async () => {
      setIsLoading((prev) => ({ ...prev, filters: true, locations: true }));

      try {
        await Promise.all([customerStore.refreshCustomer(), groupStore.refreshGroups(), filterStore.refreshFilters(), tariffGroupingStore.refreshTariffGroupings()]);
      } finally {
        setIsLoading((prev) => ({ ...prev, filters: false, locations: false }));
      }
    };

    loadData();
  }, [customerStore, groupStore, filterStore, tariffGroupingStore]);

  // Handle metric reordering
  const handleMetricReorder = (draggedMetric: MetricType, targetMetric: MetricType) => {
    // Calculate new order first
    const newOrder = [...metricOrder];
    const draggedIndex = newOrder.indexOf(draggedMetric);
    const targetIndex = newOrder.indexOf(targetMetric);

    // Remove dragged item
    newOrder.splice(draggedIndex, 1);
    // Insert at new position
    newOrder.splice(targetIndex, 0, draggedMetric);

    // Update metric order state
    setMetricOrder(newOrder);

    // Reorder existing data without reloading
    setData((prevData) => {
      const newData = [...prevData];

      // For each parent (studio/group)
      const parents = newData.filter((row) => row.level === 0);
      parents.forEach((parent) => {
        const parentIndex = newData.findIndex((row) => row.id === parent.id);
        if (parentIndex === -1) return;

        // Get all metric rows for this parent
        const metricRows = newData.filter((row) => row.parent === parent.id && row.level === 1);

        // Get all tariff rows for this parent
        const tariffRows = newData.filter((row) => {
          const metricParent = newData.find((r) => r.id === row.parent && r.level === 1);
          return metricParent && metricParent.parent === parent.id && row.level === 2;
        });

        // Remove all metric and tariff rows
        newData.splice(parentIndex + 1, metricRows.length + tariffRows.length);

        // Reinsert metric rows in new order (reversed) along with their tariff children
        [...newOrder].reverse().forEach((metricType) => {
          const metricRow = metricRows.find((row) => row.id === `${parent.id}-${metricType}`);
          if (metricRow) {
            newData.splice(parentIndex + 1, 0, metricRow);
            // If this metric has tariff children, insert them after the metric
            const metricTariffRows = tariffRows.filter((row) => row.parent === metricRow.id);
            metricTariffRows.forEach((tariffRow, index) => {
              newData.splice(parentIndex + 2 + index, 0, tariffRow);
            });
          }
        });
      });

      return newData;
    });
  };

  // Helper function to create tariff rows from API response
  const createTariffRows = (locationId: string, metricType: MetricType, apiData: any[]): RowData[] => {
    const tariffRows: RowData[] = [];
    const tariffTotals: Record<string, Record<string, number>> = {};

    // Collect all tariff data by tariff name and time label
    apiData.forEach((m: any) => {
      if (m.dailyCountByTariffMap) {
        const date = DateTime.fromISO(m.date);
        let metricLabel: string;
        const metricTypeString = metricTypeToApiString(metricType);
        const labelDate = DateTime.fromFormat("", "LLL"); // This will be set in the loop below

        Object.entries(m.dailyCountByTariffMap).forEach(([tariffName, count]) => {
          if (!tariffTotals[tariffName]) {
            tariffTotals[tariffName] = {};
          }

          // Calculate the appropriate time label
          timeLabels.forEach((label) => {
            const labelDate = DateTime.fromFormat(label, "LLL");
            const isMLSpecialCase =
              (metricTypeString === "cancellations-ml" || metricTypeString === "members-net-growth-started") &&
              date.day === date.daysInMonth &&
              date.plus({ days: 1 }).month === labelDate.month;
            const effectiveDate = isMLSpecialCase ? labelDate : date;

            let metricLabelForDate: string;
            switch (timeRange) {
              case "day":
                metricLabelForDate = effectiveDate.toFormat("dd.MM");
                break;
              case "week":
                metricLabelForDate = `KW ${effectiveDate.weekNumber}`;
                break;
              case "month":
                if (metricTypeString === "cancellations-ml" && date.day === date.daysInMonth) {
                  metricLabelForDate = date.plus({ days: 1 }).toFormat("LLL");
                } else {
                  metricLabelForDate = effectiveDate.toFormat("LLL");
                }
                break;
              default:
                metricLabelForDate = "";
            }

            if (metricLabelForDate === label) {
              tariffTotals[tariffName][label] = (tariffTotals[tariffName][label] || 0) + (count as number);
            }
          });
        });
      }
    });

    // Group tariffs if grouping term is provided or tariff groupings are selected
    const hasLegacyGrouping = tariffGrouping.trim();
    const hasNewGroupings = selectedTariffGroupings.length > 0;

    if (hasNewGroupings || hasLegacyGrouping) {
      let groupingDefinitions: { name: string; terms: string[] }[] = [];

      if (hasNewGroupings) {
        // Use selected tariff groupings from the store
        const selectedGroupings = tariffGroupingStore.tariffGroupings.filter((tg) => selectedTariffGroupings.includes(tg.id));
        groupingDefinitions = selectedGroupings.map((tg) => ({
          name: tg.name,
          terms: tg.terms,
        }));
      } else {
        // Fallback to legacy string-based grouping (each term is its own group)
        const legacyTerms = tariffGrouping
          .split(",")
          .map((term) => term.trim())
          .filter((term) => term.length > 0);

        groupingDefinitions = legacyTerms.map((term) => ({
          name: term,
          terms: [term],
        }));
      }

      const groupedTotals: Record<string, Record<string, number>> = {};
      const otherTotals: Record<string, number> = {};

      // Initialize totals for each group
      groupingDefinitions.forEach((groupDef) => {
        const groupKey = groupDef.name.toLowerCase();
        groupedTotals[groupKey] = {};
        timeLabels.forEach((label) => {
          groupedTotals[groupKey][label] = 0;
        });
      });

      // Initialize "Sonstige" totals
      timeLabels.forEach((label) => {
        otherTotals[label] = 0;
      });

      // Group tariffs
      Object.entries(tariffTotals).forEach(([tariffName, timeData]) => {
        let wasGrouped = false;

        // Check each grouping definition
        groupingDefinitions.forEach((groupDef) => {
          const groupKey = groupDef.name.toLowerCase();

          // Check if tariff contains ALL terms of this group (AND logic)
          const containsAllTerms = groupDef.terms.every((term) => tariffName.toLowerCase().includes(term.toLowerCase()));

          if (containsAllTerms) {
            timeLabels.forEach((label) => {
              const value = timeData[label] || 0;
              groupedTotals[groupKey][label] += value;
            });
            wasGrouped = true;
          }
        });

        // If not grouped into any specific group, add to "Sonstige"
        if (!wasGrouped) {
          timeLabels.forEach((label) => {
            const value = timeData[label] || 0;
            otherTotals[label] += value;
          });
        }
      });

      // Determine which groups have overlaps (tariffs that appear in multiple groups)
      const groupsWithOverlaps = new Set<string>();
      Object.entries(tariffTotals).forEach(([tariffName, timeData]) => {
        const matchingGroups = groupingDefinitions.filter((groupDef) => groupDef.terms.every((term) => tariffName.toLowerCase().includes(term.toLowerCase())));
        if (matchingGroups.length > 1) {
          matchingGroups.forEach((groupDef) => groupsWithOverlaps.add(groupDef.name.toLowerCase()));
        }
      });

      // Create group rows for each group that has data
      if (hasNewGroupings) {
        // For new groupings, use the tariffGroupingOrder for sorting
        const sortedGroupingDefinitions = [...groupingDefinitions].sort((a, b) => {
          const aIndex = tariffGroupingOrder.findIndex((id) => {
            const grouping = tariffGroupingStore.tariffGroupings.find((tg) => tg.id === id);
            return grouping?.name === a.name;
          });
          const bIndex = tariffGroupingOrder.findIndex((id) => {
            const grouping = tariffGroupingStore.tariffGroupings.find((tg) => tg.id === id);
            return grouping?.name === b.name;
          });

          if (aIndex !== -1 && bIndex !== -1) {
            return aIndex - bIndex;
          }
          if (aIndex !== -1) return -1;
          if (bIndex !== -1) return 1;
          return a.name.localeCompare(b.name, "de");
        });

        sortedGroupingDefinitions.forEach((groupDef) => {
          const groupKey = groupDef.name.toLowerCase();
          // Create group row even if there's no data
          const groupRow: RowData = {
            id: `${locationId}-${metricType}-group-${groupKey}`,
            name: groupDef.name,
            level: 2,
            parent: `${locationId}-${metricType}`,
            isGroupRow: true,
            hasOverlaps: groupsWithOverlaps.has(groupKey),
            isLoading: false,
            ...groupedTotals[groupKey],
          };
          // Initialize all time labels with 0 if they don't exist
          timeLabels.forEach((label) => {
            if (groupRow[label] === undefined) {
              groupRow[label] = 0;
            }
          });
          tariffRows.push(groupRow);
        });
      } else {
        // For legacy groupings, keep original order
        groupingDefinitions.forEach((groupDef) => {
          const groupKey = groupDef.name.toLowerCase();
          // Create group row even if there's no data
          const groupRow: RowData = {
            id: `${locationId}-${metricType}-group-${groupKey}`,
            name: groupDef.name,
            level: 2,
            parent: `${locationId}-${metricType}`,
            isGroupRow: true,
            hasOverlaps: groupsWithOverlaps.has(groupKey),
            isLoading: false,
            ...groupedTotals[groupKey],
          };
          // Initialize all time labels with 0 if they don't exist
          timeLabels.forEach((label) => {
            if (groupRow[label] === undefined) {
              groupRow[label] = 0;
            }
          });
          tariffRows.push(groupRow);
        });
      }

      // Create "Sonstige" row if there are ungrouped tariffs
      if (Object.values(otherTotals).some((value) => value > 0)) {
        const otherRow: RowData = {
          id: `${locationId}-${metricType}-group-other`,
          name: "Sonstige",
          level: 2,
          parent: `${locationId}-${metricType}`,
          isGroupRow: true,
          isLoading: false,
          ...otherTotals,
        };
        tariffRows.push(otherRow);
      }
    } else {
      // Create individual tariff rows (original behavior)
      Object.entries(tariffTotals).forEach(([tariffName, timeData]) => {
        const tariffRow: RowData = {
          id: `${locationId}-${metricType}-${tariffName}`,
          name: tariffName,
          level: 2,
          parent: `${locationId}-${metricType}`,
          tariffName: tariffName,
          isLoading: false,
          ...timeData,
        };
        tariffRows.push(tariffRow);
      });
    }

    return tariffRows;
  };

  // Hilfsfunktion: Lädt alle Metriken für alle Studios in einem Request (optional: nur bestimmte Studios)
  const loadMetricsForAllStudios = async (metricType: MetricType, studioIdsOverride?: string[]) => {
    const studioIds = studioIdsOverride ?? selectedLocations.filter((id) => id.startsWith("studio-")).map((id) => id.replace("studio-", ""));
    if (studioIds.length === 0) return;
    let metricTypeString = metricTypeToApiString(metricType);
    const studioChunks = chunkArray(studioIds, 5);
    for (const chunk of studioChunks) {
      const apiResponse = await apiService.get<Record<string, Record<number, any[]>>>(
        `/metrics/batch-metrics?studioIds=${chunk.join(",")}&metricTypes=${metricTypeString}&startDate=${startDate}&endDate=${endDate}${
          selectedFilters ? "&filterIds=" + selectedFilters : ""
        }`
      );
      setMetricsDataCache((prev) => ({
        ...prev,
        [metricTypeString]: {
          ...(prev[metricTypeString] || {}),
          ...(apiResponse[metricTypeString] || {}),
        },
      }));
      setData((prevData) => {
        const newData = [...prevData];
        chunk.forEach((studioId) => {
          const locationId = `studio-${studioId}`;
          const metricIndex = newData.findIndex((row) => row.id === `${locationId}-${metricType}`);
          if (metricIndex !== -1) {
            const studioData = apiResponse[metricTypeString]?.[Number(studioId)];
            if (studioData) {
              const updatedRow = { ...newData[metricIndex] };
              timeLabels.forEach((label) => {
                const values = studioData.filter((m: any) => {
                  const date = DateTime.fromISO(m.date);
                  let metricLabel: string;
                  const labelDate = DateTime.fromFormat(label, "LLL");
                  const isMLSpecialCase =
                    (metricTypeString === "cancellations-ml" || metricTypeString === "members-net-growth-started") &&
                    date.day === date.daysInMonth &&
                    date.plus({ days: 1 }).month === labelDate.month;
                  const effectiveDate = isMLSpecialCase ? labelDate : date;
                  switch (timeRange) {
                    case "day":
                      metricLabel = effectiveDate.toFormat("dd.MM");
                      break;
                    case "week":
                      metricLabel = `KW ${effectiveDate.weekNumber}`;
                      break;
                    case "month":
                      if (metricTypeString === "cancellations-ml" && date.day === date.daysInMonth) {
                        metricLabel = date.plus({ days: 1 }).toFormat("LLL");
                      } else {
                        metricLabel = effectiveDate.toFormat("LLL");
                      }
                      break;
                    default:
                      metricLabel = "";
                  }
                  return metricLabel === label;
                });
                const sum = values.reduce((total: number, m: any) => total + (m.dailyCount || 0), 0);
                updatedRow[label] = sum;
              });
              updatedRow.isLoading = false;
              newData[metricIndex] = updatedRow;

              // If this is NEW_MEMBERS_WITH_NAMES, create tariff rows
              if (metricType === MetricType.NEW_MEMBERS_WITH_NAMES) {
                // Remove any existing tariff rows for this metric
                const existingTariffIndices = newData
                  .map((row, index) => ({ row, index }))
                  .filter(({ row }) => row.parent === `${locationId}-${metricType}` && row.level === 2)
                  .map(({ index }) => index)
                  .sort((a, b) => b - a); // Sort in descending order to remove from end first

                existingTariffIndices.forEach((index) => {
                  newData.splice(index, 1);
                });

                // Create new tariff rows
                const tariffRows = createTariffRows(locationId, metricType, studioData);

                // Insert tariff rows after the metric row
                const insertIndex = newData.findIndex((row) => row.id === `${locationId}-${metricType}`) + 1;
                tariffRows.forEach((tariffRow, index) => {
                  newData.splice(insertIndex + index, 0, tariffRow);
                });
              }

              // If this is NEW_CLOSINGS_WITH_NAMES, create tariff rows
              if (metricType === MetricType.NEW_CLOSINGS_WITH_NAMES) {
                // Remove any existing tariff rows for this metric
                const existingTariffIndices = newData
                  .map((row, index) => ({ row, index }))
                  .filter(({ row }) => row.parent === `${locationId}-${metricType}` && row.level === 2)
                  .map(({ index }) => index)
                  .sort((a, b) => b - a); // Sort in descending order to remove from end first

                existingTariffIndices.forEach((index) => {
                  newData.splice(index, 1);
                });

                // Create new tariff rows
                const tariffRows = createTariffRows(locationId, metricType, studioData);

                // Insert tariff rows after the metric row
                const insertIndex = newData.findIndex((row) => row.id === `${locationId}-${metricType}`) + 1;
                tariffRows.forEach((tariffRow, index) => {
                  newData.splice(insertIndex + index, 0, tariffRow);
                });
              }

              // If this is MODULE_CONTRACTS_WITH_NAMES, create tariff rows
              if (metricType === MetricType.MODULE_CONTRACTS_WITH_NAMES) {
                // Remove any existing tariff rows for this metric
                const existingTariffIndices = newData
                  .map((row, index) => ({ row, index }))
                  .filter(({ row }) => row.parent === `${locationId}-${metricType}` && row.level === 2)
                  .map(({ index }) => index)
                  .sort((a, b) => b - a); // Sort in descending order to remove from end first

                existingTariffIndices.forEach((index) => {
                  newData.splice(index, 1);
                });

                // Create new tariff rows
                const tariffRows = createTariffRows(locationId, metricType, studioData);

                // Insert tariff rows after the metric row
                const insertIndex = newData.findIndex((row) => row.id === `${locationId}-${metricType}`) + 1;
                tariffRows.forEach((tariffRow, index) => {
                  newData.splice(insertIndex + index, 0, tariffRow);
                });
              }
            }
          }
        });
        return newData;
      });
    }
  };

  // Hilfsfunktion: Lädt alle Metriken für alle Gruppen in einem Request (optional: nur bestimmte Gruppen)
  const loadMetricsForAllGroups = async (metricType: MetricType, groupIdsOverride?: string[]) => {
    const groupIds = groupIdsOverride ?? selectedLocations.filter((id) => id.startsWith("group-")).map((id) => id.replace("group-", ""));
    if (groupIds.length === 0) return;

    let metricTypeString = metricTypeToApiString(metricType);
    const groupChunks = chunkArray(groupIds, 5);

    for (const chunk of groupChunks) {
      const apiResponse = await apiService.get<Record<string, Record<string, any[]>>>(
        `/metrics/batch-metrics?studioGroupIds=${chunk.join(",")}&metricTypes=${metricTypeString}&startDate=${startDate}&endDate=${endDate}${
          selectedFilters ? "&filterIds=" + selectedFilters : ""
        }`
      );

      setMetricsDataCache((prev) => ({
        ...prev,
        [metricTypeString + "-groups"]: {
          ...(prev[metricTypeString + "-groups"] || {}),
          ...(apiResponse[metricTypeString] || {}),
        },
      }));

      setData((prevData) => {
        const newData = [...prevData];
        groupIds.forEach((groupId) => {
          const locationId = `group-${groupId}`;
          const metricIndex = newData.findIndex((row) => row.id === `${locationId}-${metricType}`);
          if (metricIndex !== -1) {
            const group = groupStore.groups.find((g) => g.id.toString() === groupId);
            if (!group) return;

            const updatedRow = { ...newData[metricIndex] };
            timeLabels.forEach((label) => {
              const groupData = apiResponse[metricTypeString][groupId];
              if (groupData) {
                const values = groupData.filter((m: any) => {
                  const date = DateTime.fromISO(m.date);
                  let metricLabel: string;
                  const labelDate = DateTime.fromFormat(label, "LLL");
                  const isMLSpecialCase =
                    (metricTypeString === "cancellations-ml" || metricTypeString === "members-net-growth-started") &&
                    date.day === date.daysInMonth &&
                    date.plus({ days: 1 }).month === labelDate.month;
                  const effectiveDate = isMLSpecialCase ? labelDate : date;
                  switch (timeRange) {
                    case "day":
                      metricLabel = effectiveDate.toFormat("dd.MM");
                      break;
                    case "week":
                      metricLabel = `KW ${effectiveDate.weekNumber}`;
                      break;
                    case "month":
                      if (metricTypeString === "cancellations-ml" && date.day === date.daysInMonth) {
                        metricLabel = date.plus({ days: 1 }).toFormat("LLL");
                      } else {
                        metricLabel = effectiveDate.toFormat("LLL");
                      }
                      break;
                    default:
                      metricLabel = "";
                  }
                  return metricLabel === label;
                });
                const sum = values.reduce((total: number, m: any) => total + (m.dailyCount || 0), 0);
                updatedRow[label] = sum;
              }
            });
            updatedRow.isLoading = false;
            newData[metricIndex] = updatedRow;

            // If this is NEW_MEMBERS_WITH_NAMES, create tariff rows
            if (metricType === MetricType.NEW_MEMBERS_WITH_NAMES) {
              const groupData = apiResponse[metricTypeString][groupId];
              if (groupData) {
                // Remove any existing tariff rows for this metric
                const existingTariffIndices = newData
                  .map((row, index) => ({ row, index }))
                  .filter(({ row }) => row.parent === `${locationId}-${metricType}` && row.level === 2)
                  .map(({ index }) => index)
                  .sort((a, b) => b - a); // Sort in descending order to remove from end first

                existingTariffIndices.forEach((index) => {
                  newData.splice(index, 1);
                });

                // Create new tariff rows
                const tariffRows = createTariffRows(locationId, metricType, groupData);

                // Insert tariff rows after the metric row
                const insertIndex = newData.findIndex((row) => row.id === `${locationId}-${metricType}`) + 1;
                tariffRows.forEach((tariffRow, index) => {
                  newData.splice(insertIndex + index, 0, tariffRow);
                });
              }
            }

            // If this is NEW_CLOSINGS_WITH_NAMES, create tariff rows
            if (metricType === MetricType.NEW_CLOSINGS_WITH_NAMES) {
              const groupData = apiResponse[metricTypeString][groupId];
              if (groupData) {
                // Remove any existing tariff rows for this metric
                const existingTariffIndices = newData
                  .map((row, index) => ({ row, index }))
                  .filter(({ row }) => row.parent === `${locationId}-${metricType}` && row.level === 2)
                  .map(({ index }) => index)
                  .sort((a, b) => b - a); // Sort in descending order to remove from end first

                existingTariffIndices.forEach((index) => {
                  newData.splice(index, 1);
                });

                // Create new tariff rows
                const tariffRows = createTariffRows(locationId, metricType, groupData);

                // Insert tariff rows after the metric row
                const insertIndex = newData.findIndex((row) => row.id === `${locationId}-${metricType}`) + 1;
                tariffRows.forEach((tariffRow, index) => {
                  newData.splice(insertIndex + index, 0, tariffRow);
                });
              }
            }

            // If this is MODULE_CONTRACTS_WITH_NAMES, create tariff rows
            if (metricType === MetricType.MODULE_CONTRACTS_WITH_NAMES) {
              const groupData = apiResponse[metricTypeString][groupId];
              if (groupData) {
                // Remove any existing tariff rows for this metric
                const existingTariffIndices = newData
                  .map((row, index) => ({ row, index }))
                  .filter(({ row }) => row.parent === `${locationId}-${metricType}` && row.level === 2)
                  .map(({ index }) => index)
                  .sort((a, b) => b - a); // Sort in descending order to remove from end first

                existingTariffIndices.forEach((index) => {
                  newData.splice(index, 1);
                });

                // Create new tariff rows
                const tariffRows = createTariffRows(locationId, metricType, groupData);

                // Insert tariff rows after the metric row
                const insertIndex = newData.findIndex((row) => row.id === `${locationId}-${metricType}`) + 1;
                tariffRows.forEach((tariffRow, index) => {
                  newData.splice(insertIndex + index, 0, tariffRow);
                });
              }
            }
          }
        });
        return newData;
      });
    }
  };

  // Behandlung von Location-Änderungen
  const handleLocationChange = (newLocations: string[]) => {
    // Finde hinzugefügte und entfernte Locations
    const addedLocations = newLocations.filter((loc) => !selectedLocations.includes(loc));
    const removedLocations = selectedLocations.filter((loc) => !newLocations.includes(loc));

    setSelectedLocations(newLocations);

    setData((prevData) => {
      const newData = [...prevData];
      // Entferne alle Zeilen für Locations, die nicht mehr ausgewählt sind (including tariff rows)
      for (let i = newData.length - 1; i >= 0; i--) {
        const row = newData[i];
        let rowLocationId = row.level === 0 ? row.id : row.parent;

        // For tariff rows (level 2), we need to find the parent location through the metric row
        if (row.level === 2) {
          const metricRow = newData.find((r) => r.id === row.parent && r.level === 1);
          if (metricRow) {
            rowLocationId = metricRow.parent;
          }
        }

        if (rowLocationId && removedLocations.includes(rowLocationId)) {
          newData.splice(i, 1);
        }
      }
      // Füge neue Locations hinzu
      addedLocations.forEach((locationId) => {
        let locationName = locationId; // Default: ID als Platzhalter
        if (locationId.startsWith("studio-")) {
          const studioId = locationId.replace("studio-", "");
          const studio = customerStore.studios.find((s) => s.id.toString() === studioId);
          if (studio) locationName = studio.name;
        } else if (locationId.startsWith("group-")) {
          // Speichere die Gruppen-ID als Platzhalter, der Name wird später im Renderer aufgelöst
          locationName = locationId; // z.B. group-123
        }
        newData.push({
          id: locationId,
          name: locationName,
          level: 0,
          isExpanded: true,
          isLoading: false,
        });
        const parentIndex = newData.length - 1;
        [...metricOrder].reverse().forEach((metricType) => {
          if (selectedMetrics.includes(metricType.toString())) {
            const metricRow: RowData = {
              id: `${locationId}-${metricType}`,
              name: goalStore.getMetricTypeLabel(metricType),
              level: 1,
              parent: locationId,
              metricType: metricType.toString(),
              isLoading: true,
              isExpanded: false, // Metrics start collapsed
            };
            newData.splice(parentIndex + 1, 0, metricRow);
          }
        });
      });
      return newData;
    });

    // Batching-Logik für Studios
    const addedStudios = addedLocations.filter((id) => id.startsWith("studio-"));
    if (addedStudios.length > 1) {
      metricOrder.forEach((metricType) => {
        if (selectedMetrics.includes(metricType.toString())) {
          loadMetricsForAllStudios(
            metricType,
            addedStudios.map((id) => id.replace("studio-", ""))
          );
        }
      });
    } else if (addedStudios.length === 1) {
      metricOrder.forEach((metricType) => {
        if (selectedMetrics.includes(metricType.toString())) {
          loadMetricForLocation(addedStudios[0], metricType);
        }
      });
    }
    // Batching-Logik für Gruppen
    const addedGroups = addedLocations.filter((id) => id.startsWith("group-"));
    if (addedGroups.length > 1) {
      metricOrder.forEach((metricType) => {
        if (selectedMetrics.includes(metricType.toString())) {
          loadMetricsForAllGroups(
            metricType,
            addedGroups.map((id) => id.replace("group-", ""))
          );
        }
      });
    } else if (addedGroups.length === 1) {
      metricOrder.forEach((metricType) => {
        if (selectedMetrics.includes(metricType.toString())) {
          loadMetricForLocation(addedGroups[0], metricType);
        }
      });
    }
  };

  // Funktion zum Laden einer spezifischen Metrik für ein bestimmtes Studio oder eine Gruppe
  const loadMetricForLocation = useCallback(
    async (locationId: string, metricType: MetricType) => {
      // Generiere einen eindeutigen Schlüssel für diese Kombination
      const key = `${locationId}-${metricType}`;

      // Abbrechen laufender Anfragen für diese Kombination
      if (abortControllersRef.current[key]) {
        abortControllersRef.current[key].abort();
      }

      // Neuen Abort Controller erstellen
      abortControllersRef.current[key] = new AbortController();
      const signal = abortControllersRef.current[key].signal;

      // Setze den Loading-Status nur für diese spezifische Metrik
      setData((prevData) => {
        const newData = [...prevData];
        const metricIndex = newData.findIndex((row) => row.id === `${locationId}-${metricType}`);

        if (metricIndex !== -1) {
          // Kopie des Objekts erstellen, um Mutationen zu vermeiden
          const updatedRow = { ...newData[metricIndex], isLoading: true };
          newData[metricIndex] = updatedRow;
        }

        return newData;
      });

      try {
        // Metriktyp zu String konvertieren
        const metricTypeString = metricTypeToApiString(metricType);

        // Ermittle Studio- oder Gruppen-ID
        let apiResponse: Record<string, Record<number, any[]>>;

        if (locationId.startsWith("studio-")) {
          // Studio Metrik laden
          const studioId = locationId.replace("studio-", "");

          // API-Call für ein einzelnes Studio
          apiResponse = await apiService.get<Record<string, Record<number, any[]>>>(
            `/metrics/batch-metrics?studioIds=${studioId}&metricTypes=${metricTypeString}&startDate=${startDate}&endDate=${endDate}${
              selectedFilters ? "&filterIds=" + selectedFilters : ""
            }`,
            { signal }
          );

          // Cache aktualisieren
          setMetricsDataCache((prev) => ({
            ...prev,
            [metricTypeString]: {
              ...(prev[metricTypeString] || {}),
              ...(apiResponse[metricTypeString] || {}),
            },
          }));

          // Daten aktualisieren
          if (!signal.aborted) {
            setData((prevData) => {
              const newData = [...prevData];
              const metricIndex = newData.findIndex((row) => row.id === `${locationId}-${metricType}`);

              if (metricIndex !== -1) {
                const studioData = apiResponse[metricTypeString]?.[Number(studioId)];

                if (studioData) {
                  // Kopie des Objekts erstellen, um Mutationen zu vermeiden
                  const updatedRow = { ...newData[metricIndex] };

                  // Werte für jeden Zeitraum aktualisieren
                  timeLabels.forEach((label) => {
                    const values = studioData.filter((m: any) => {
                      const date = DateTime.fromISO(m.date);
                      let metricLabel: string;

                      // Magicline-Sonderfall: Letzter Tag des Vormonats zählt zum aktuellen Monat
                      const labelDate = DateTime.fromFormat(label, "LLL");
                      const isMLSpecialCase =
                        (metricTypeString === "cancellations-ml" || metricTypeString === "members-net-growth-started") &&
                        date.day === date.daysInMonth && // Letzter Tag des Monats
                        date.plus({ days: 1 }).month === labelDate.month; // Nächster Tag ist im aktuellen Monat

                      // Wenn es der ML-Sonderfall ist, verwende das Label-Datum für die Gruppierung
                      const effectiveDate = isMLSpecialCase ? labelDate : date;

                      switch (timeRange) {
                        case "day":
                          metricLabel = effectiveDate.toFormat("dd.MM");
                          break;
                        case "week":
                          metricLabel = `KW ${effectiveDate.weekNumber}`;
                          break;
                        case "month":
                          // Für ML: Wenn es der letzte Tag des Monats ist, zähle ihn zum nächsten Monat
                          if (metricTypeString === "cancellations-ml" && date.day === date.daysInMonth) {
                            metricLabel = date.plus({ days: 1 }).toFormat("LLL");
                          } else {
                            metricLabel = effectiveDate.toFormat("LLL");
                          }
                          break;
                        default:
                          metricLabel = "";
                      }
                      return metricLabel === label;
                    });

                    // Summiere alle dailyCount-Werte für diesen Zeitraum
                    const sum = values.reduce((total: number, m: any) => total + (m.dailyCount || 0), 0);
                    updatedRow[label] = sum;
                  });

                  // Loading-Status zurücksetzen
                  updatedRow.isLoading = false;

                  // Aktualisierte Zeile einfügen
                  newData[metricIndex] = updatedRow;

                  // If this is NEW_MEMBERS_WITH_NAMES, create tariff rows
                  if (metricType === MetricType.NEW_MEMBERS_WITH_NAMES) {
                    // Remove any existing tariff rows for this metric
                    const existingTariffIndices = newData
                      .map((row, index) => ({ row, index }))
                      .filter(({ row }) => row.parent === `${locationId}-${metricType}` && row.level === 2)
                      .map(({ index }) => index)
                      .sort((a, b) => b - a); // Sort in descending order to remove from end first

                    existingTariffIndices.forEach((index) => {
                      newData.splice(index, 1);
                    });

                    // Create new tariff rows
                    const tariffRows = createTariffRows(locationId, metricType, studioData);

                    // Insert tariff rows after the metric row
                    const insertIndex = newData.findIndex((row) => row.id === `${locationId}-${metricType}`) + 1;
                    tariffRows.forEach((tariffRow, index) => {
                      newData.splice(insertIndex + index, 0, tariffRow);
                    });
                  }

                  // If this is NEW_CLOSINGS_WITH_NAMES, create tariff rows
                  if (metricType === MetricType.NEW_CLOSINGS_WITH_NAMES) {
                    // Remove any existing tariff rows for this metric
                    const existingTariffIndices = newData
                      .map((row, index) => ({ row, index }))
                      .filter(({ row }) => row.parent === `${locationId}-${metricType}` && row.level === 2)
                      .map(({ index }) => index)
                      .sort((a, b) => b - a); // Sort in descending order to remove from end first

                    existingTariffIndices.forEach((index) => {
                      newData.splice(index, 1);
                    });

                    // Create new tariff rows
                    const tariffRows = createTariffRows(locationId, metricType, studioData);

                    // Insert tariff rows after the metric row
                    const insertIndex = newData.findIndex((row) => row.id === `${locationId}-${metricType}`) + 1;
                    tariffRows.forEach((tariffRow, index) => {
                      newData.splice(insertIndex + index, 0, tariffRow);
                    });
                  }
                }
              }

              return newData;
            });
          }
        } else if (locationId.startsWith("group-")) {
          // Gruppen Metrik laden (jetzt immer mit studioGroupIds wie im Initial-Load)
          const groupId = locationId.replace("group-", "");

          // API-Call für die Gruppe mit studioGroupIds
          apiResponse = await apiService.get<Record<string, Record<number, any[]>>>(
            `/metrics/batch-metrics?studioGroupIds=${groupId}&metricTypes=${metricTypeString}&startDate=${startDate}&endDate=${endDate}${
              selectedFilters ? "&filterIds=" + selectedFilters : ""
            }`,
            { signal }
          );

          // Cache aktualisieren (wie in loadMetricsForAllGroups)
          setMetricsDataCache((prev) => ({
            ...prev,
            [metricTypeString + "-groups"]: {
              ...(prev[metricTypeString + "-groups"] || {}),
              ...(apiResponse[metricTypeString] || {}),
            },
          }));

          // Daten aktualisieren
          if (!signal.aborted) {
            setData((prevData) => {
              const newData = [...prevData];
              const metricIndex = newData.findIndex((row) => row.id === `${locationId}-${metricType}`);

              if (metricIndex !== -1) {
                // Kopie des Objekts erstellen, um Mutationen zu vermeiden
                const updatedRow = { ...newData[metricIndex] };

                // Werte für jeden Zeitraum berechnen
                timeLabels.forEach((label) => {
                  const groupData = Object.values(apiResponse[metricTypeString] || {})[0];
                  if (groupData) {
                    const values = groupData.filter((m: any) => {
                      const date = DateTime.fromISO(m.date);
                      let metricLabel: string;
                      const labelDate = DateTime.fromFormat(label, "LLL");
                      const isMLSpecialCase =
                        (metricTypeString === "cancellations-ml" || metricTypeString === "members-net-growth-started") &&
                        date.day === date.daysInMonth &&
                        date.plus({ days: 1 }).month === labelDate.month;
                      const effectiveDate = isMLSpecialCase ? labelDate : date;
                      switch (timeRange) {
                        case "day":
                          metricLabel = effectiveDate.toFormat("dd.MM");
                          break;
                        case "week":
                          metricLabel = `KW ${effectiveDate.weekNumber}`;
                          break;
                        case "month":
                          if (metricTypeString === "cancellations-ml" && date.day === date.daysInMonth) {
                            metricLabel = date.plus({ days: 1 }).toFormat("LLL");
                          } else {
                            metricLabel = effectiveDate.toFormat("LLL");
                          }
                          break;
                        default:
                          metricLabel = "";
                      }
                      return metricLabel === label;
                    });
                    const sum = values.reduce((total: number, m: any) => total + (m.dailyCount || 0), 0);
                    updatedRow[label] = sum;
                  }
                });
                updatedRow.isLoading = false;
                newData[metricIndex] = updatedRow;

                // If this is NEW_MEMBERS_WITH_NAMES, create tariff rows
                if (metricType === MetricType.NEW_MEMBERS_WITH_NAMES) {
                  const groupData = Object.values(apiResponse[metricTypeString] || {})[0];
                  if (groupData) {
                    // Remove any existing tariff rows for this metric
                    const existingTariffIndices = newData
                      .map((row, index) => ({ row, index }))
                      .filter(({ row }) => row.parent === `${locationId}-${metricType}` && row.level === 2)
                      .map(({ index }) => index)
                      .sort((a, b) => b - a); // Sort in descending order to remove from end first

                    existingTariffIndices.forEach((index) => {
                      newData.splice(index, 1);
                    });

                    // Create new tariff rows
                    const tariffRows = createTariffRows(locationId, metricType, groupData);

                    // Insert tariff rows after the metric row
                    const insertIndex = newData.findIndex((row) => row.id === `${locationId}-${metricType}`) + 1;
                    tariffRows.forEach((tariffRow, index) => {
                      newData.splice(insertIndex + index, 0, tariffRow);
                    });
                  }
                }

                // If this is NEW_CLOSINGS_WITH_NAMES, create tariff rows
                if (metricType === MetricType.NEW_CLOSINGS_WITH_NAMES) {
                  const groupData = Object.values(apiResponse[metricTypeString] || {})[0];
                  if (groupData) {
                    // Remove any existing tariff rows for this metric
                    const existingTariffIndices = newData
                      .map((row, index) => ({ row, index }))
                      .filter(({ row }) => row.parent === `${locationId}-${metricType}` && row.level === 2)
                      .map(({ index }) => index)
                      .sort((a, b) => b - a); // Sort in descending order to remove from end first

                    existingTariffIndices.forEach((index) => {
                      newData.splice(index, 1);
                    });

                    // Create new tariff rows
                    const tariffRows = createTariffRows(locationId, metricType, groupData);

                    // Insert tariff rows after the metric row
                    const insertIndex = newData.findIndex((row) => row.id === `${locationId}-${metricType}`) + 1;
                    tariffRows.forEach((tariffRow, index) => {
                      newData.splice(insertIndex + index, 0, tariffRow);
                    });
                  }
                }
              }
              return newData;
            });
          }
        }
      } catch (error) {
        if (signal && !signal.aborted) {
          console.error(`Error loading metric ${metricType} for ${locationId}:`, error);

          // Loading-Status im Fehlerfall zurücksetzen
          setData((prevData) => {
            const newData = [...prevData];
            const metricIndex = newData.findIndex((row) => row.id === `${locationId}-${metricType}`);

            if (metricIndex !== -1) {
              // Kopie des Objekts erstellen, um Mutationen zu vermeiden
              const updatedRow = { ...newData[metricIndex], isLoading: false };
              newData[metricIndex] = updatedRow;
            }

            return newData;
          });
        }
      } finally {
        // Cleanup
        if (signal && !signal.aborted) {
          delete abortControllersRef.current[key];
        }
      }
    },
    [startDate, endDate, timeRange, selectedFilters, customerStore, groupStore, timeLabels, tariffGrouping]
  );

  // Handle metric selection (Batching für mehrere KPIs)
  const handleMetricToggle = (value: MetricType) => {
    setSelectedMetrics((prev) => {
      const valueStr = value.toString();
      const metricWasSelected = prev.includes(valueStr);
      const newSelected = metricWasSelected ? prev.filter((v) => v !== valueStr) : [...prev, valueStr];
      setData((prevData) => {
        const newData = [...prevData];
        const parents = newData.filter((row) => row.level === 0);
        parents.forEach((parent) => {
          const parentIndex = newData.findIndex((row) => row.id === parent.id);
          if (parentIndex === -1) return;
          if (metricWasSelected) {
            // Remove the metric row and any tariff rows if it was deselected
            const metricIndex = newData.findIndex((row) => row.parent === parent.id && row.metricType === valueStr);
            if (metricIndex !== -1) {
              // First remove any tariff rows (level 2) for this metric
              const tariffIndices = newData
                .map((row, index) => ({ row, index }))
                .filter(({ row }) => row.parent === `${parent.id}-${value}` && row.level === 2)
                .map(({ index }) => index)
                .sort((a, b) => b - a); // Sort in descending order to remove from end first

              tariffIndices.forEach((index) => {
                newData.splice(index, 1);
              });

              // Then remove the metric row itself
              const updatedMetricIndex = newData.findIndex((row) => row.parent === parent.id && row.metricType === valueStr);
              if (updatedMetricIndex !== -1) {
                newData.splice(updatedMetricIndex, 1);
              }
            }
          } else {
            // Add new metric row with loading state if metric was selected
            const metricRow: RowData = {
              id: `${parent.id}-${value}`,
              name: goalStore.getMetricTypeLabel(value),
              level: 1,
              parent: parent.id,
              metricType: valueStr,
              isLoading: true,
              isExpanded: false, // Metrics start collapsed
            };
            const insertIndex = determineInsertIndex(newData, parentIndex, value, metricOrder);
            newData.splice(insertIndex, 0, metricRow);
          }
        });
        return newData;
      });
      // Batching-Logik für mehrere KPIs
      if (!metricWasSelected) {
        // Prüfe, wie viele KPIs neu ausgewählt wurden
        const newlySelected = newSelected.filter((v) => !prev.includes(v));
        if (newlySelected.length > 1) {
          // Studios
          const studioIds = selectedLocations.filter((id) => id.startsWith("studio-")).map((id) => id.replace("studio-", ""));
          newlySelected.forEach((metricTypeStr) => {
            loadMetricsForAllStudios(metricTypeStr as MetricType, studioIds);
          });
          // Gruppen
          const groupIds = selectedLocations.filter((id) => id.startsWith("group-")).map((id) => id.replace("group-", ""));
          newlySelected.forEach((metricTypeStr) => {
            loadMetricsForAllGroups(metricTypeStr as MetricType, groupIds);
          });
        } else if (newlySelected.length === 1) {
          // Einzel-Request für alle Locations
          selectedLocations.forEach((locationId) => {
            loadMetricForLocation(locationId, newlySelected[0] as MetricType);
          });
        }
      }
      return newSelected;
    });
  };

  // Hilfsfunktion zur Bestimmung des Einfügeindex basierend auf Metrikreihenfolge
  const determineInsertIndex = (data: RowData[], parentIndex: number, metricType: MetricType, order: MetricType[]) => {
    const metricOrderIndex = order.indexOf(metricType);
    let insertIndex = parentIndex + 1;

    // Durchlaufe existierende Metriken für diesen Parent
    for (let i = parentIndex + 1; i < data.length; i++) {
      if (data[i].level === 0) {
        break; // Wir sind beim nächsten Parent angekommen
      }

      if (data[i].level === 1 && data[i].parent === data[parentIndex].id) {
        const rowMetricType = data[i].metricType as MetricType;
        const rowOrderIndex = order.indexOf(rowMetricType as MetricType);

        if (rowOrderIndex > metricOrderIndex) {
          return i; // Füge vor dieser Metrik ein
        }

        // Count all children (tariff rows) of this metric
        let childCount = 0;
        for (let j = i + 1; j < data.length; j++) {
          if (data[j].level === 2 && data[j].parent === data[i].id) {
            childCount++;
          } else {
            break;
          }
        }
        insertIndex = i + 1 + childCount;
      }
    }

    return insertIndex;
  };

  // State für Initial-Load-Flag
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Initial-Load: Nur auf isInitialLoad hören!
  useEffect(() => {
    if (!isInitialLoad) return;
    // Datenstruktur aufbauen
    const initialData: RowData[] = [];
    for (const locationId of selectedLocations) {
      let locationName = locationId;
      if (locationId.startsWith("studio-")) {
        const studioId = locationId.replace("studio-", "");
        const studio = customerStore.studios.find((s) => s.id.toString() === studioId);
        if (studio) locationName = studio.name;
      } else if (locationId.startsWith("group-")) {
        locationName = locationId;
      }
      initialData.push({
        id: locationId,
        name: locationName,
        level: 0,
        isExpanded: true,
        isLoading: false,
      });
      const parentIndex = initialData.length - 1;
      [...metricOrder].reverse().forEach((metricType) => {
        if (selectedMetrics.includes(metricType.toString())) {
          const metricRow: RowData = {
            id: `${locationId}-${metricType}`,
            name: goalStore.getMetricTypeLabel(metricType as MetricType),
            level: 1,
            parent: locationId,
            metricType: metricType.toString(),
            isLoading: true,
            isExpanded: false, // Metrics start collapsed
          };
          initialData.splice(parentIndex + 1, 0, metricRow);
        }
      });
    }
    setData(initialData);
    // Studios gebündelt laden
    for (const metricType of selectedMetrics) {
      loadMetricsForAllStudios(metricType as MetricType);
      loadMetricsForAllGroups(metricType as MetricType);
    }
    setIsInitialLoad(false);
  }, [isInitialLoad]);

  // Benutzerdefinierte Stile für die Tabelle
  const customStyles: TableStyles = {
    table: {
      style: {
        backgroundColor: "#1f1f1f",
        color: "#e2e2e2",
        borderRadius: "0",
      },
    },
    header: {
      style: {
        fontSize: "1rem",
        color: "#e2e2e2",
        backgroundColor: "#1f1f1f",
        minHeight: "56px",
      },
    },
    headRow: {
      style: {
        backgroundColor: "#1f1f1f",
        borderBottomWidth: "1px",
        borderBottomColor: "#292929",
        borderBottomStyle: "solid",
        color: "#e2e2e2",
        fontWeight: "normal",
      },
    },
    headCells: {
      style: {
        paddingLeft: "16px",
        paddingRight: "16px",
        fontSize: "0.875rem",
        fontWeight: "normal",
        color: "#e2e2e2",
      },
    },
    rows: {
      style: {
        fontSize: "0.875rem",
        fontWeight: "400",
        backgroundColor: "#1f1f1f",
        color: "#e2e2e2",
        minHeight: "48px",
        "&:not(:last-of-type)": {
          borderBottomStyle: "solid",
          borderBottomWidth: "1px",
          borderBottomColor: "#292929",
        },
        "&:hover": {
          backgroundColor: "#222222",
          cursor: "pointer",
        },
      },
      stripedStyle: {
        backgroundColor: "#1f1f1f",
        color: "#e2e2e2",
      },
    },
    cells: {
      style: {
        paddingLeft: "16px",
        paddingRight: "16px",
        color: "#e2e2e2",
      },
    },
    pagination: {
      style: {
        backgroundColor: "#1f1f1f",
        color: "#e2e2e2",
        borderTopStyle: "solid",
        borderTopWidth: "1px",
        borderTopColor: "#292929",
        borderRadius: "0",
      },
      pageButtonsStyle: {
        color: "#e2e2e2",
        fill: "#e2e2e2",
        backgroundColor: "transparent",
        "&:disabled": {
          color: "#444444",
          fill: "#444444",
        },
        "&:hover:not(:disabled)": {
          backgroundColor: "#232323",
        },
        "&:focus": {
          outline: "none",
          backgroundColor: "#232323",
        },
      },
    },
  };

  // Add a function to render the empty state
  const renderEmptyState = (message: string, buttonText?: string, onButtonClick?: () => void) => (
    <div className="bg-[#1f1f1f] mb-4 p-8 flex flex-col items-center justify-center min-h-[300px]">
      <div className="text-lg text-gray-400 mb-4">{message}</div>
      {buttonText && onButtonClick && (
        <button className="btn btn-primary btn-sm" onClick={onButtonClick}>
          {buttonText}
        </button>
      )}
    </div>
  );

  // State for export options
  const [exportOptions, setExportOptions] = useState({
    includeSums: true,
    includeTariffNames: true,
  });
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);

  // Add this function before the return statement
  const handleExport = () => {
    let allRows = getVisibleRows();

    // If includeTariffNames is true, we need to include all tariff/grouping rows, even if not expanded
    if (
      exportOptions.includeTariffNames &&
      (selectedMetrics.includes(MetricType.NEW_MEMBERS_WITH_NAMES.toString()) || selectedMetrics.includes(MetricType.NEW_CLOSINGS_WITH_NAMES.toString()))
    ) {
      // Get all data rows (not just visible ones)
      const allDataRows = data.filter((row) => {
        // Include all level 0 and 1 rows
        if (row.level <= 1) return true;
        // Include level 2 rows (tariff/grouping rows) that have a NEW_MEMBERS_WITH_NAMES or NEW_CLOSINGS_WITH_NAMES parent
        if (row.level === 2) {
          const parentRow = data.find((r) => r.id === row.parent && r.level === 1);
          return parentRow?.metricType === MetricType.NEW_MEMBERS_WITH_NAMES.toString() || parentRow?.metricType === MetricType.NEW_CLOSINGS_WITH_NAMES.toString();
        }
        return false;
      });

      // Apply search filter if active
      if (searchTerm.trim()) {
        const searchLower = searchTerm.toLowerCase();
        const filteredParents = allDataRows
          .filter((row) => row.level === 0)
          .filter((row) => {
            if (row.id.startsWith("group-")) {
              const group = groupStore.groups.find((g) => `group-${g.id}` === row.id);
              return group?.groupName?.toLowerCase().includes(searchLower);
            }
            return row.name.toLowerCase().includes(searchLower);
          });

        // Include all children of matching parents
        allRows = [];
        filteredParents.forEach((parent) => {
          allRows.push(parent);
          // Add metric children
          const metricChildren = allDataRows.filter((row) => row.parent === parent.id && row.level === 1);
          metricChildren.forEach((metric) => {
            allRows.push(metric);
            // Add tariff children for NEW_MEMBERS_WITH_NAMES and NEW_CLOSINGS_WITH_NAMES
            if (metric.metricType === MetricType.NEW_MEMBERS_WITH_NAMES.toString() || metric.metricType === MetricType.NEW_CLOSINGS_WITH_NAMES.toString()) {
              const tariffChildren = allDataRows.filter((row) => row.parent === metric.id && row.level === 2);
              allRows.push(...tariffChildren);
            }
          });
        });
      } else {
        // No search filter - include all data according to locationOrder
        const parentRows = allDataRows.filter((row) => row.level === 0);
        const sortedParentRows = parentRows.sort((a, b) => {
          const aIndex = locationOrder.indexOf(a.id);
          const bIndex = locationOrder.indexOf(b.id);
          if (aIndex !== -1 && bIndex !== -1) {
            return aIndex - bIndex;
          }
          if (aIndex !== -1) return -1;
          if (bIndex !== -1) return 1;
          return a.name.localeCompare(b.name, "de");
        });

        allRows = [];
        sortedParentRows.forEach((parent) => {
          allRows.push(parent);
          // Add metric children
          const metricChildren = allDataRows.filter((row) => row.parent === parent.id && row.level === 1);
          metricChildren.forEach((metric) => {
            allRows.push(metric);
            // Add tariff children for NEW_MEMBERS_WITH_NAMES and NEW_CLOSINGS_WITH_NAMES
            if (metric.metricType === MetricType.NEW_MEMBERS_WITH_NAMES.toString() || metric.metricType === MetricType.NEW_CLOSINGS_WITH_NAMES.toString()) {
              const tariffChildren = allDataRows.filter((row) => row.parent === metric.id && row.level === 2);
              allRows.push(...tariffChildren);
            }
          });
        });
      }
    }
    // Filter out tariff rows if includeTariffNames is false
    const filteredRows = exportOptions.includeTariffNames ? allRows : allRows.filter((row) => row.level !== 2);

    const exportData = filteredRows.map((row) => {
      let displayName = row.name;
      if (row.level === 0 && row.id.startsWith("group-")) {
        const group = groupStore.groups.find((g) => `group-${g.id}` === row.id);
        displayName = group?.groupName || row.name;
      }
      const rowData: any = {
        Name: displayName,
      };
      let sum = 0;
      timeLabels.forEach((label) => {
        if (row.level === 0) {
          rowData[label] = "";
        } else {
          const value = row[label] || 0;
          rowData[label] = value;
          sum += value;
        }
      });
      // Horizontale Summe für Metrik-Zeilen und Tariff-Zeilen
      rowData["Summe"] = row.level === 1 || row.level === 2 ? sum : "";
      return rowData;
    });

    // Wenn nur eine Kennzahl ausgewählt ist, füge die Werte direkt in die Studio-Zeilen ein
    if (selectedMetrics.length === 1) {
      const metricType = selectedMetrics[0];

      // If includeTariffNames is enabled and we have NEW_MEMBERS_WITH_NAMES or NEW_CLOSINGS_WITH_NAMES, keep the hierarchical structure
      if (exportOptions.includeTariffNames && (metricType === MetricType.NEW_MEMBERS_WITH_NAMES.toString() || metricType === MetricType.NEW_CLOSINGS_WITH_NAMES.toString())) {
        // Keep the existing exportData as-is (with Level 0, 1, and 2 rows)
      } else {
        // Erstelle neue Export-Daten mit den Werten direkt in den Studio-Zeilen
        const newExportData = exportData.filter((row) => {
          // Finde die entsprechende Studio-Zeile in den sichtbaren Zeilen
          const studioRow = allRows.find((r) => {
            if (r.level !== 0) return false;
            if (r.id.startsWith("group-")) {
              const group = groupStore.groups.find((g) => `group-${g.id}` === r.id);
              return (group?.groupName || r.name) === row.Name;
            }
            return r.name === row.Name;
          });

          if (studioRow) {
            // Finde die entsprechende Metrik-Zeile für dieses Studio
            const metricRow = allRows.find((r) => r.level === 1 && r.parent === studioRow.id && r.metricType === metricType);

            // Füge die Werte zur Studio-Zeile hinzu
            if (metricRow) {
              let sum = 0;
              timeLabels.forEach((label) => {
                row[label] = metricRow[label] || 0;
                sum += Number(row[label]) || 0;
              });
              row["Summe"] = sum;
            }
            return true; // Behalte nur die Studio-Zeilen
          }
          return false; // Entferne alle anderen Zeilen (Metrik-Zeilen)
        });

        // Aktualisiere die Export-Daten
        exportData.length = 0;
        exportData.push(...newExportData);
      }

      // Füge Summenzeile hinzu, wenn die Option aktiviert ist
      if (exportOptions.includeSums) {
        const sumRow: any = {
          Name: `Summe ${goalStore.getMetricTypeLabel(metricType as MetricType)}`,
        };

        timeLabels.forEach((label) => {
          sumRow[label] = exportData.filter((row) => row.Name !== sumRow.Name).reduce((sum, row) => sum + (row[label] || 0), 0);
        });
        sumRow["Summe"] = timeLabels.reduce((sum, label) => sum + (sumRow[label] || 0), 0);

        exportData.push(sumRow);
      }
    } else {
      // Füge eine Leerzeile ein (nur bei mehreren Kennzahlen)
      const emptyRow: any = {
        Name: "",
      };
      timeLabels.forEach((label) => {
        emptyRow[label] = "";
      });
      exportData.push(emptyRow);

      // Gruppiere die Metriken nach KPI-Typ (nur bei mehreren Kennzahlen)
      const metricsByType = allRows
        .filter((row) => row.level === 1 && row.metricType)
        .reduce((acc, row) => {
          const metricType = row.metricType as string;
          if (!acc[metricType]) {
            acc[metricType] = [];
          }
          acc[metricType].push(row);
          return acc;
        }, {} as Record<string, RowData[]>);

      // Berechne Summen für jeden KPI-Typ, wenn die Option aktiviert ist
      if (exportOptions.includeSums) {
        Object.entries(metricsByType).forEach(([metricType, rows]) => {
          const sumRow: any = {
            Name: `Summe ${goalStore.getMetricTypeLabel(metricType as MetricType)}`,
          };

          timeLabels.forEach((label) => {
            sumRow[label] = rows.reduce((sum, row) => sum + (row[label] || 0), 0);
          });

          exportData.push(sumRow);
        });
      }
    }

    // Create CSV content
    const headers = ["Name", ...timeLabels, "Summe"];
    const csvContent = [headers.join(";"), ...exportData.map((row) => headers.map((header) => row[header]).join(";"))].join("\n");

    // Create and trigger download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `analytics-export-${DateTime.now().toFormat("yyyy-MM-dd")}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Schließe den Dialog nach dem Export
    setIsExportDialogOpen(false);
  };

  // Add effect to close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest(".metrics-dropdown") && !target.closest(".time-range-dropdown")) {
        setIsMetricsDropdownOpen(false);
        setIsTimeRangeDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Save date range to localStorage when it changes
  useEffect(() => {
    localStorage.setItem("analyticsStartDate", startDate);
  }, [startDate]);

  useEffect(() => {
    localStorage.setItem("analyticsEndDate", endDate);
  }, [endDate]);

  // Save time range to localStorage when it changes
  useEffect(() => {
    localStorage.setItem("analyticsTimeRange", timeRange);
  }, [timeRange]);

  // Save selected filters to localStorage when they change
  useEffect(() => {
    localStorage.setItem("analyticsSelectedFilters", JSON.stringify([selectedFilters]));
  }, [selectedFilters]);

  // Save selected locations to localStorage when they change
  useEffect(() => {
    localStorage.setItem("analyticsSelectedLocations", JSON.stringify(selectedLocations));
  }, [selectedLocations]);

  // Save selected metrics to localStorage when they change
  useEffect(() => {
    localStorage.setItem("analyticsSelectedMetrics", JSON.stringify(selectedMetrics));
  }, [selectedMetrics]);

  // Save tariff grouping to localStorage when it changes
  useEffect(() => {
    localStorage.setItem("analyticsTariffGrouping", tariffGrouping);
  }, [tariffGrouping]);

  // Save selected tariff groupings to localStorage when they change
  useEffect(() => {
    localStorage.setItem("analyticsSelectedTariffGroupings", JSON.stringify(selectedTariffGroupings));
  }, [selectedTariffGroupings]);

  // State for import dialog
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [importOrder, setImportOrder] = useState("");

  const handleImport = () => {
    try {
      const parsedOrder = JSON.parse(importOrder);
      if (Array.isArray(parsedOrder)) {
        setLocationOrder(parsedOrder);
        localStorage.setItem("analyticsLocationOrder", JSON.stringify(parsedOrder));
        setIsImportDialogOpen(false);
      } else {
        alert("Ungültiges Format. Bitte geben Sie ein gültiges JSON-Array ein.");
      }
    } catch (e) {
      alert("Fehler beim Parsen des JSON. Bitte überprüfen Sie das Format.");
    }
  };

  // Funktion: Erstelle Tariff Rows aus dem Cache neu (für Gruppierungsänderungen)
  const updateTariffRowsFromCache = () => {
    setData((prevData) => {
      const newData = [...prevData];
      const metricTypes = [MetricType.NEW_MEMBERS_WITH_NAMES, MetricType.NEW_CLOSINGS_WITH_NAMES];

      // Für jede Location und jeden Metrik-Typ die Tariff Rows neu erstellen
      metricTypes.forEach((metricType) => {
        if (!selectedMetrics.includes(metricType.toString())) return;

        const metricTypeString = metricTypeToApiString(metricType);

        selectedLocations.forEach((locationId) => {
          const metricRowId = `${locationId}-${metricType}`;
          const metricRowIndex = newData.findIndex((row) => row.id === metricRowId);

          if (metricRowIndex === -1) return;

          // Cache-Daten holen
          let cacheKey = metricTypeString;
          let apiData: any[] = [];

          if (locationId.startsWith("studio-")) {
            const studioId = locationId.replace("studio-", "");
            const cache = metricsDataCache[cacheKey];
            if (cache && cache[studioId]) {
              apiData = cache[studioId];
            }
          } else if (locationId.startsWith("group-")) {
            const groupId = locationId.replace("group-", "");
            cacheKey = metricTypeString + "-groups";
            const cache = metricsDataCache[cacheKey];
            if (cache && cache[groupId]) {
              apiData = cache[groupId];
            }
          }

          if (apiData.length > 0) {
            // Entferne bestehende Tariff Rows für diese Metrik
            const existingTariffIndices = newData
              .map((row, index) => ({ row, index }))
              .filter(({ row }) => row.parent === metricRowId && row.level === 2)
              .map(({ index }) => index)
              .sort((a, b) => b - a); // Sort in descending order to remove from end first

            existingTariffIndices.forEach((index) => {
              newData.splice(index, 1);
            });

            // Erstelle neue Tariff Rows aus Cache-Daten
            const tariffRows = createTariffRows(locationId, metricType, apiData);

            // Füge neue Tariff Rows nach der Metrik-Row ein
            const insertIndex = newData.findIndex((row) => row.id === metricRowId) + 1;
            tariffRows.forEach((tariffRow, index) => {
              newData.splice(insertIndex + index, 0, tariffRow);
            });
          }
        });
      });

      return newData;
    });
  };

  // Funktion: Aggregiere die Werte aus dem Cache für die aktuelle Zeitbasis neu
  const updateAggregatedData = () => {
    setData((prevData) => {
      const newData = [...prevData];
      newData.forEach((row) => {
        if (row.level === 1 && row.metricType && row.parent) {
          // Handle metric rows (level 1)
          let locationId = row.parent;
          let metricType = row.metricType as MetricType;

          const metricTypeString = metricTypeToApiString(metricType);
          let cacheKey = metricTypeString;
          let studioId: string | undefined = undefined;
          let groupId: string | undefined = undefined;
          if (locationId.startsWith("studio-")) {
            studioId = locationId.replace("studio-", "");
          } else if (locationId.startsWith("group-")) {
            groupId = locationId.replace("group-", "");
            cacheKey = metricTypeString + "-groups";
          }

          const cache = metricsDataCache[cacheKey];
          let apiData: any[] = [];

          if (studioId && cache && cache[studioId]) {
            apiData = cache[studioId];
          } else if (groupId && cache && cache[groupId]) {
            apiData = cache[groupId];
          }

          if (apiData.length > 0) {
            // Regular metric row aggregation
            timeLabels.forEach((label) => {
              const values = apiData.filter((m: any) => {
                const date = DateTime.fromISO(m.date);
                let metricLabel: string;
                const labelDate = DateTime.fromFormat(label, "LLL");
                const isMLSpecialCase =
                  (metricTypeString === "cancellations-ml" || metricTypeString === "members-net-growth-started") &&
                  date.day === date.daysInMonth &&
                  date.plus({ days: 1 }).month === labelDate.month;
                const effectiveDate = isMLSpecialCase ? labelDate : date;
                switch (timeRange) {
                  case "day":
                    metricLabel = effectiveDate.toFormat("dd.MM");
                    break;
                  case "week":
                    metricLabel = `KW ${effectiveDate.weekNumber}`;
                    break;
                  case "month":
                    if (metricTypeString === "cancellations-ml" && date.day === date.daysInMonth) {
                      metricLabel = date.plus({ days: 1 }).toFormat("LLL");
                    } else {
                      metricLabel = effectiveDate.toFormat("LLL");
                    }
                    break;
                  default:
                    metricLabel = "";
                }
                return metricLabel === label;
              });
              const sum = values.reduce((total: number, m: any) => total + (m.dailyCount || 0), 0);
              row[label] = sum;
            });
          }
        } else if (row.level === 2 && row.tariffName && row.parent) {
          // Handle tariff rows (level 2) separately
          const metricRow = newData.find((r) => r.id === row.parent && r.level === 1);
          if (metricRow && metricRow.parent && metricRow.metricType) {
            const locationId = metricRow.parent;
            const metricType = metricRow.metricType as MetricType;
            const metricTypeString = metricTypeToApiString(metricType);

            let cacheKey = metricTypeString;
            let studioId: string | undefined = undefined;
            let groupId: string | undefined = undefined;

            if (locationId.startsWith("studio-")) {
              studioId = locationId.replace("studio-", "");
            } else if (locationId.startsWith("group-")) {
              groupId = locationId.replace("group-", "");
              cacheKey = metricTypeString + "-groups";
            }

            const cache = metricsDataCache[cacheKey];
            let apiData: any[] = [];

            if (studioId && cache && cache[studioId]) {
              apiData = cache[studioId];
            } else if (groupId && cache && cache[groupId]) {
              apiData = cache[groupId];
            }

            if (apiData.length > 0) {
              // Tariff row aggregation
              timeLabels.forEach((label) => {
                const values = apiData.filter((m: any) => {
                  const date = DateTime.fromISO(m.date);
                  let metricLabel: string;
                  const labelDate = DateTime.fromFormat(label, "LLL");
                  const isMLSpecialCase =
                    (metricTypeString === "cancellations-ml" || metricTypeString === "members-net-growth-started") &&
                    date.day === date.daysInMonth &&
                    date.plus({ days: 1 }).month === labelDate.month;
                  const effectiveDate = isMLSpecialCase ? labelDate : date;
                  switch (timeRange) {
                    case "day":
                      metricLabel = effectiveDate.toFormat("dd.MM");
                      break;
                    case "week":
                      metricLabel = `KW ${effectiveDate.weekNumber}`;
                      break;
                    case "month":
                      if (metricTypeString === "cancellations-ml" && date.day === date.daysInMonth) {
                        metricLabel = date.plus({ days: 1 }).toFormat("LLL");
                      } else {
                        metricLabel = effectiveDate.toFormat("LLL");
                      }
                      break;
                    default:
                      metricLabel = "";
                  }
                  return metricLabel === label;
                });

                const sum = values.reduce((total: number, m: any) => {
                  if (m.dailyCountByTariffMap && m.dailyCountByTariffMap[row.tariffName!]) {
                    return total + m.dailyCountByTariffMap[row.tariffName!];
                  }
                  return total;
                }, 0);
                row[label] = sum;
              });
            }
          }
        }
      });
      return newData;
    });
  };

  // State für letzten Ladevorgang und Lade-Status
  const [lastLoaded, setLastLoaded] = useState<Date | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const lastRefreshRef = useRef<number>(0);

  // Add state for throttling
  const [isThrottled, setIsThrottled] = useState(false);

  // Setze lastLoaded, wenn alle Zeilen fertig geladen sind und kein Spinner mehr sichtbar ist
  useEffect(() => {
    if (!isInitialLoad && data.length > 0 && data.every((row) => !row.isLoading)) {
      setLastLoaded(new Date());
    }
  }, [data, isInitialLoad]);

  // Hilfsfunktion: Daten manuell neu laden (wie bei Zeitraumwechsel)
  const manualRefresh = async () => {
    if (isRefreshing || isThrottled) return;
    setIsThrottled(true);
    setTimeout(() => setIsThrottled(false), 3000);
    setIsRefreshing(true);
    lastRefreshRef.current = Date.now();
    for (const metricType of selectedMetrics) {
      await loadMetricsForAllStudios(metricType as MetricType);
      await loadMetricsForAllGroups(metricType as MetricType);
    }
    setIsRefreshing(false);
    setLastLoaded(new Date());
  };

  // Setze lastLoaded auch nach automatischem Neuladen
  useEffect(() => {
    if (isInitialLoad) return;
    setIsRefreshing(true);
    Promise.all(
      selectedMetrics.map(async (metricType) => {
        await loadMetricsForAllStudios(metricType as MetricType);
        await loadMetricsForAllGroups(metricType as MetricType);
      })
    ).then(() => {
      setIsRefreshing(false);
      setLastLoaded(new Date());
    });
    // eslint-disable-next-line
  }, [startDate, endDate, selectedFilters]);

  // Aggregiere die Daten aus dem Cache neu, wenn sich die Ansicht (Tag/Woche/Monat) ändert
  useEffect(() => {
    if (isInitialLoad || isRefreshing) return;

    // Check if we have NEW_MEMBERS_WITH_NAMES with groupings
    if (selectedMetrics.includes(MetricType.NEW_MEMBERS_WITH_NAMES.toString()) && (selectedTariffGroupings.length > 0 || tariffGrouping.trim())) {
      // Use tariff row recreation for grouped data
      updateTariffRowsFromCache();
    }

    // Update all other data
    updateAggregatedData();
  }, [timeRange, timeLabels.join(), metricOrder.join(), locationOrder.join()]);

  // Recreate tariff rows when tariff grouping changes
  useEffect(() => {
    if (isInitialLoad || isRefreshing) return;
    if (!selectedMetrics.includes(MetricType.NEW_MEMBERS_WITH_NAMES.toString()) && !selectedMetrics.includes(MetricType.NEW_CLOSINGS_WITH_NAMES.toString())) return;

    // Use cache-based tariff row recreation instead of new API requests
    updateTariffRowsFromCache();
  }, [tariffGrouping]);

  // Recreate tariff rows when selected tariff groupings change (cache-based)
  useEffect(() => {
    if (isInitialLoad || isRefreshing) return;
    if (!selectedMetrics.includes(MetricType.NEW_MEMBERS_WITH_NAMES.toString()) && !selectedMetrics.includes(MetricType.NEW_CLOSINGS_WITH_NAMES.toString())) return;

    // Use cache-based tariff row recreation instead of new API requests
    updateTariffRowsFromCache();
  }, [selectedTariffGroupings]);

  // Add state for tariff grouping order
  const [tariffGroupingOrder, setTariffGroupingOrder] = useState<string[]>(() => {
    try {
      const savedOrder = localStorage.getItem("analyticsTariffGroupingOrder");
      if (savedOrder) {
        const parsed = JSON.parse(savedOrder);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      }
    } catch (e) {
      console.error("Fehler beim Laden der Tariff Grouping-Sortierung:", e);
    }
    return [];
  });

  // Save tariff grouping order to localStorage when it changes
  useEffect(() => {
    localStorage.setItem("analyticsTariffGroupingOrder", JSON.stringify(tariffGroupingOrder));
  }, [tariffGroupingOrder]);

  // Effect to update tariff grouping order when available groupings change
  useEffect(() => {
    if (tariffGroupingStore.tariffGroupings.length === 0) return;

    const allAvailableGroupings = tariffGroupingStore.tariffGroupings.map((tg) => tg.id);

    // Only update order if it's empty
    if (tariffGroupingOrder.length === 0) {
      const newOrder = [...allAvailableGroupings].sort((a, b) => {
        const aGrouping = tariffGroupingStore.tariffGroupings.find((tg) => tg.id === a);
        const bGrouping = tariffGroupingStore.tariffGroupings.find((tg) => tg.id === b);
        return (aGrouping?.name || "").localeCompare(bGrouping?.name || "", "de");
      });

      setTariffGroupingOrder(newOrder);
      return;
    }

    // Check if new groupings have been added
    const missingGroupings = allAvailableGroupings.filter((id) => !tariffGroupingOrder.includes(id));
    if (missingGroupings.length > 0) {
      const newOrder = [...tariffGroupingOrder, ...missingGroupings];
      setTariffGroupingOrder(newOrder);
    }

    // Remove groupings that no longer exist
    const existingGroupings = tariffGroupingOrder.filter((id) => allAvailableGroupings.includes(id));
    if (existingGroupings.length !== tariffGroupingOrder.length) {
      setTariffGroupingOrder(existingGroupings);
    }
  }, [tariffGroupingStore.tariffGroupings, tariffGroupingOrder]);

  // Recreate tariff rows when tariff grouping order changes (cache-based)
  useEffect(() => {
    if (isInitialLoad || isRefreshing) return;
    if (!selectedMetrics.includes(MetricType.NEW_MEMBERS_WITH_NAMES.toString()) && !selectedMetrics.includes(MetricType.NEW_CLOSINGS_WITH_NAMES.toString())) return;
    if (selectedTariffGroupings.length === 0) return; // Only for new groupings

    // Use cache-based tariff row recreation to apply new order
    updateTariffRowsFromCache();
  }, [tariffGroupingOrder]);

  // Handle tariff grouping reordering
  const handleTariffGroupingReorder = (draggedGroupingId: string, targetGroupingId: string) => {
    const newOrder = [...tariffGroupingOrder];
    const draggedIndex = newOrder.indexOf(draggedGroupingId);
    const targetIndex = newOrder.indexOf(targetGroupingId);

    if (draggedIndex !== -1 && targetIndex !== -1) {
      newOrder.splice(draggedIndex, 1);
      newOrder.splice(targetIndex, 0, draggedGroupingId);
      setTariffGroupingOrder(newOrder);
    }
  };

  return (
    <SidebarPageLayout title="Analytics" onRefresh={manualRefresh} className="w-full" refreshDisabled={isRefreshing || isThrottled}>
      <span className="ml-1 text-xs text-gray-400 block mb-2">
        {isRefreshing ? "Lädt..." : lastLoaded ? `Zuletzt geladen: ${lastLoaded.toLocaleTimeString()}` : "Noch nie geladen"}
      </span>

      {/* Controls Row */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-2">
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="input input-bordered input-sm h-10" />
          <span className="mx-2">bis</span>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="input input-bordered input-sm h-10" />
        </div>
        <div className="w-64">
          <SearchBar onSearch={setSearchTerm} placeholder="Suchen..." paramName="analyticsSearch" />
        </div>
      </div>

      {/* Table Header Row */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center">
          <h2 className="text-lg font-semibold">Tabelle</h2>
        </div>

        <div className="flex items-center gap-4">
          {/* New Tariff Groupings Dropdown */}
          <MultiSelectDropdown
            icon={AdjustmentsHorizontalIcon}
            selectedValues={selectedTariffGroupings}
            onChange={setSelectedTariffGroupings}
            options={tariffGroupingOrder
              .map((groupingId) => {
                const grouping = tariffGroupingStore.tariffGroupings.find((tg) => tg.id === groupingId);
                return {
                  value: groupingId,
                  label: grouping?.name || "Unknown",
                };
              })
              .filter((option) => option.label !== "Unknown")}
            placeholder="Gruppierungen"
            emptyText="Gruppierungen"
            isLoading={isLoading.filters}
            searchable={true}
            searchPlaceholder="Gruppierung suchen..."
            showSelectAll={true}
            draggable={true}
            onDragStart={(e, value) => {
              e.dataTransfer.setData("text/plain", value);
            }}
            onDragEnd={(e) => {
              e.currentTarget.classList.remove("opacity-50");
            }}
            onDragOver={(e) => {
              e.preventDefault();
            }}
            onDragLeave={(e) => {
              e.preventDefault();
            }}
            onDrop={(e, value) => {
              e.preventDefault();
              const draggedGroupingId = e.dataTransfer.getData("text/plain");
              handleTariffGroupingReorder(draggedGroupingId, value);
            }}
          />
          <MultiSelectDropdown
            icon={AdjustmentsHorizontalIcon}
            selectedValues={selectedFilters ? [selectedFilters] : []}
            onChange={(values) => {
              setSelectedFilters(values[0] || "");
            }}
            options={filterStore.filters.map((filter) => ({
              value: filter.id,
              label: filter.name,
            }))}
            placeholder="Quick Filter"
            emptyText="Quick Filter: Keine"
            isLoading={isLoading.filters}
            singleSelect={true}
            showSelectAll={false}
          />
          <MultiSelectDropdown
            icon={MapPinIcon}
            selectedValues={selectedLocations}
            onChange={handleLocationChange}
            options={[
              ...customerStore.studios.map((studio) => ({
                value: `studio-${studio.id}`,
                label: studio.name,
              })),
              ...groupStore.groups.map((group) => ({
                value: `group-${group.id}`,
                label: group.groupName,
              })),
            ].sort((a, b) => {
              // Wenn eine benutzerdefinierte Sortierung existiert, verwende diese
              if (locationOrder.length > 0) {
                const aIndex = locationOrder.indexOf(a.value);
                const bIndex = locationOrder.indexOf(b.value);
                if (aIndex !== -1 && bIndex !== -1) {
                  return aIndex - bIndex;
                }
                if (aIndex !== -1) return -1;
                if (bIndex !== -1) return 1;
              }
              // Ansonsten alphabetisch sortieren
              return a.label.localeCompare(b.label, "de");
            })}
            placeholder="Standorte"
            emptyText="Standorte: Alle"
            isLoading={isLoading.locations}
            searchable={true}
            searchPlaceholder="Standorte suchen..."
            draggable={true}
            onDragStart={(e, value) => {
              e.dataTransfer.setData("text/plain", value);
            }}
            onDragEnd={(e) => {
              e.currentTarget.classList.remove("opacity-50");
            }}
            onDragOver={(e) => {
              e.preventDefault();
            }}
            onDragLeave={(e) => {
              e.preventDefault();
            }}
            onDrop={(e, value) => {
              e.preventDefault();
              const draggedLocation = e.dataTransfer.getData("text/plain");
              handleLocationReorder(draggedLocation, value);
            }}
          />

          {/* Icon Buttons with Background Color */}
          <div className="flex gap-2">
            <div className="time-range-dropdown">
              <MultiSelectDropdown
                icon={CalendarIcon}
                selectedValues={[timeRange]}
                onChange={(values) => {
                  if (values.length > 0) {
                    setTimeRange(values[0] as "day" | "week" | "month");
                  }
                }}
                options={[
                  { value: "day", label: "Tag" },
                  { value: "week", label: "Woche" },
                  { value: "month", label: "Monat" },
                ]}
                placeholder="Zeitraum"
                emptyText="Zeitraum"
                variant="icon"
                searchable={false}
                showSelectAll={false}
                singleSelect={true}
              />
            </div>
            <div className="metrics-dropdown">
              <MultiSelectDropdown
                icon={FlagIcon}
                selectedValues={selectedMetrics}
                onChange={(values) => {
                  values.forEach((value) => {
                    if (!selectedMetrics.includes(value)) {
                      handleMetricToggle(value as MetricType);
                    }
                  });
                  selectedMetrics.forEach((value) => {
                    if (!values.includes(value)) {
                      handleMetricToggle(value as MetricType);
                    }
                  });
                }}
                options={metricOrder.map((metricType) => {
                  return {
                    value: metricType.toString(),
                    label: goalStore.getMetricTypeLabel(metricType as MetricType),
                  };
                })}
                placeholder="Kennzahlen"
                emptyText="Kennzahlen"
                variant="icon"
                searchable={true}
                showSelectAll={true}
                draggable={true}
                onDragStart={(e, value) => {
                  e.dataTransfer.setData("text/plain", value);
                }}
                onDragEnd={(e) => {
                  e.currentTarget.classList.remove("opacity-50");
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                }}
                onDrop={(e, value) => {
                  e.preventDefault();
                  const draggedMetric = e.dataTransfer.getData("text/plain") as MetricType;
                  handleMetricReorder(draggedMetric, value as MetricType);
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Tariff Grouping Input - only show if NEW_MEMBERS_WITH_NAMES or NEW_CLOSINGS_WITH_NAMES is selected */}
      {(selectedMetrics.includes(MetricType.NEW_MEMBERS_WITH_NAMES.toString()) || selectedMetrics.includes(MetricType.NEW_CLOSINGS_WITH_NAMES.toString())) && (
        <div className="space-y-3 mb-4 p-3 bg-[#1f1f1f] rounded">
          {/* Legacy Text Input - only show if no new groupings are selected */}
          {selectedTariffGroupings.length === 0 && (
            <div className="flex items-center gap-2">
              <label htmlFor="tariffGrouping" className="text-sm text-gray-300 whitespace-nowrap">
                Manuelle Gruppierung:
              </label>
              <input
                id="tariffGrouping"
                type="text"
                value={tariffGrouping}
                onChange={(e) => setTariffGrouping(e.target.value)}
                placeholder="z.B. Black, Red, Online (Mehrere Begriffe durch Komma trennen)"
                className="input input-bordered input-sm flex-1 h-8"
              />
            </div>
          )}
        </div>
      )}

      {/* Main Content - Data Table or Empty State */}
      {selectedMetrics.length === 0 || selectedLocations.length === 0 ? (
        renderEmptyState(
          selectedMetrics.length === 0 ? "Bitte wählen Sie mindestens eine Kennzahl aus" : "Bitte wählen Sie mindestens einen Standort aus",
          selectedMetrics.length === 0 ? "Kennzahlen auswählen" : "Standorte auswählen",
          selectedMetrics.length === 0 ? () => setIsMetricsDropdownOpen(true) : undefined
        )
      ) : getVisibleRows().length === 0 ? (
        renderEmptyState("Keine Ergebnisse gefunden", "Suche zurücksetzen", () => setSearchTerm(""))
      ) : (
        <div className="bg-[#1f1f1f] mb-4">
          <DataTable
            columns={generateColumns()}
            data={getVisibleRows()}
            progressPending={false} // We're now handling loading state per row
            pointerOnHover
            noHeader
            className="analytics-table"
            customStyles={customStyles}
            paginationComponentOptions={{
              rowsPerPageText: "Zeilen pro Seite:",
              rangeSeparatorText: "von",
            }}
          />
        </div>
      )}

      {/* Footer with Export Button */}
      <div className="flex justify-end gap-2">
        <button className="btn btn-neutral btn-md flex items-center gap-2" onClick={() => setIsImportDialogOpen(true)}>
          <ArrowUpTrayIcon className="w-4 h-4" />
          Sortierung importieren
        </button>
        <button className="btn btn-neutral btn-md flex items-center gap-2" onClick={() => setIsExportDialogOpen(true)} disabled={getVisibleRows().length === 0}>
          <ArrowDownTrayIcon className="w-4 h-4" />
          Als CSV exportieren
        </button>
      </div>

      {/* Export Dialog */}
      {isExportDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-[#1f1f1f] p-6 rounded-lg shadow-lg w-96">
            <h3 className="text-lg font-medium mb-4">Export-Optionen</h3>

            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="includeSums"
                  className="checkbox checkbox-sm mr-3"
                  checked={exportOptions.includeSums}
                  onChange={(e) => setExportOptions((prev) => ({ ...prev, includeSums: e.target.checked }))}
                />
                <label htmlFor="includeSums" className="text-sm">
                  KPI Summenzeilen einbeziehen
                </label>
              </div>

              {/* Show tariff names option only if NEW_MEMBERS_WITH_NAMES or NEW_CLOSINGS_WITH_NAMES is selected */}
              {(selectedMetrics.includes(MetricType.NEW_MEMBERS_WITH_NAMES.toString()) || selectedMetrics.includes(MetricType.NEW_CLOSINGS_WITH_NAMES.toString())) && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="includeTariffNames"
                    className="checkbox checkbox-sm mr-3"
                    checked={exportOptions.includeTariffNames}
                    onChange={(e) => setExportOptions((prev) => ({ ...prev, includeTariffNames: e.target.checked }))}
                  />
                  <label htmlFor="includeTariffNames" className="text-sm">
                    Ausgeklappte Tarifnamen einbeziehen
                  </label>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button className="btn btn-sm btn-ghost" onClick={() => setIsExportDialogOpen(false)}>
                Abbrechen
              </button>
              <button className="btn btn-sm btn-primary" onClick={handleExport}>
                Exportieren
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Dialog */}
      {isImportDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-[#1f1f1f] p-6 rounded-lg shadow-lg w-96">
            <h3 className="text-lg font-medium mb-4">Import-Optionen</h3>
            <textarea
              className="textarea textarea-bordered w-full h-40 mb-4"
              value={importOrder}
              onChange={(e) => setImportOrder(e.target.value)}
              placeholder="Fügen Sie hier die JSON-Reihenfolge ein"
            />
            <div className="flex justify-end gap-2">
              <button className="btn btn-sm btn-ghost" onClick={() => setIsImportDialogOpen(false)}>
                Abbrechen
              </button>
              <button className="btn btn-sm btn-primary" onClick={handleImport}>
                Importieren
              </button>
            </div>
          </div>
        </div>
      )}
    </SidebarPageLayout>
  );
});

export default AnalyticsPage;
